import React, { useState, useEffect, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Button } from './ui/button';
import { Dialog, DialogContent, DialogTitle, DialogDescription } from './ui/dialog';
import { X, ArrowRight, Sparkle } from '@phosphor-icons/react';
import { listTourSteps, getTourProgress, completeTour, markTourStepDone } from '../services/api';

const TOUR_KEY = 'oscop_tour_seen_v1';
const hasBusinessDialog = () => Boolean(document.querySelector('[role="dialog"][data-state="open"]:not([data-tour-dialog="true"]), [role="alertdialog"][data-state="open"]'));

// Fallback si l'API tour_steps est indisponible.
const DEFAULT_STEPS = [
  {
    title: 'Bienvenue dans le CRM ESS O\'SCOP',
    body: "Ce mini-tour vous fait découvrir les 5 endroits qui vous rendront le plus productifs.",
    tone: 'from-[#2D5A4C] to-[#1E4136]',
    action_path: null, action_label: null,
  },
  {
    title: 'Tableau de bord',
    body: 'Retrouvez ici tous les indicateurs clés : prospects, membres, dossiers ouverts, contributions du mois — mis à jour en temps réel.',
    tone: 'from-[#00AEEF] to-[#2D5A4C]',
    action_path: '/dashboard', action_label: 'Ouvrir le tableau de bord',
  },
  {
    title: 'Dossiers ESS',
    body: "Créez, suivez et documentez vos dossiers. Utilisez l'Assistant IA complet (dégradé vert-cyan) pour préremplir un dossier en 2 phrases.",
    tone: 'from-[#80C51F] to-[#2D5A4C]',
    action_path: '/dossiers', action_label: 'Voir les dossiers',
  },
  {
    title: 'Adhésion coopérative',
    body: 'Envoyez des kits d\'adhésion, suivez les candidatures sur le Kanban et générez les certificats officiels.',
    tone: 'from-[#FF8E00] to-[#EA3C3A]',
    action_path: '/adhesion', action_label: 'Ouvrir l\'adhésion',
  },
  {
    title: 'Recherche IA (Ctrl+K)',
    body: 'Posez une question en langage naturel : « dossiers de Guadeloupe » ou « contacts de la mairie de Fort-de-France ».',
    tone: 'from-[#D5007D] to-[#EA3C3A]',
    action_path: null, action_label: null, hint: 'Appuyez sur Ctrl+K à tout moment',
  },
];

export function AppTour() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const userKey = user?.id || user?._id || user?.email;
  const automaticAttempt = useRef(null);
  const explicitOpen = useRef(false);
  const inOto = location.pathname === '/oto' || location.pathname.startsWith('/oto/');
  const [step, setStep] = useState(0);
  const [open, setOpen] = useState(false);
  const [steps, setSteps] = useState(DEFAULT_STEPS);
  const [videoIdx, setVideoIdx] = useState(0);

  useEffect(() => {
    if (!userKey) return;
    let cancelled = false;
    listTourSteps().then((r) => {
      if (!cancelled && Array.isArray(r.data?.items) && r.data.items.length) setSteps(r.data.items);
    }).catch(() => { /* garde fallback */ });
    return () => { cancelled = true; };
  }, [userKey]);

  useEffect(() => {
    if (!userKey) return;
    if (inOto) { if (!explicitOpen.current) setOpen(false); return; }
    if (automaticAttempt.current === userKey) return;
    let cancelled = false;
    const maybeOpen = async () => {
      try {
        if (localStorage.getItem(TOUR_KEY) === '1') { automaticAttempt.current = userKey; return; }
        const progress = await getTourProgress().catch(() => null);
        if (cancelled) return;
        automaticAttempt.current = userKey;
        if (progress?.data?.tour_completed) { localStorage.setItem(TOUR_KEY, '1'); return; }
        if (localStorage.getItem(TOUR_KEY) !== '1' && !hasBusinessDialog() && !explicitOpen.current) {
          explicitOpen.current = false; setOpen(true);
        }
      } catch { /* stockage local indisponible : ne pas interrompre le travail */ }
    };
    maybeOpen();
    return () => { cancelled = true; };
  }, [userKey, inOto]);

  // Ecoute demande d'ouverture depuis TourProgressBar (clic "Sauter à cette étape")
  useEffect(() => {
    const handler = (e) => {
      if (hasBusinessDialog()) return;
      const idx = Number(e?.detail?.index ?? 0);
      try { localStorage.removeItem(TOUR_KEY); } catch {}
      setStep(Math.max(0, Math.min(idx, steps.length - 1)));
      explicitOpen.current = true;
      setOpen(true);
    };
    window.addEventListener('oscop:tour:open', handler);
    return () => window.removeEventListener('oscop:tour:open', handler);
  }, [steps.length]);

  useEffect(() => {
    if (!open) return;
    // A ZIP import can finish after a manual tour was opened. Business dialogs
    // always take priority; pausing the guide does not mark it as completed.
    const pauseForDialog = () => {
      if (hasBusinessDialog()) { explicitOpen.current = false; setOpen(false); }
    };
    const observer = new MutationObserver(pauseForDialog);
    observer.observe(document.body, { childList: true, subtree: true, attributes: true, attributeFilter: ['data-state', 'role'] });
    pauseForDialog();
    return () => observer.disconnect();
  }, [open]);

  useEffect(() => { setVideoIdx(0); }, [step]);

  const close = () => {
    try { localStorage.setItem(TOUR_KEY, '1'); } catch {}
    completeTour().catch(() => { /* silencieux — la notification est un bonus */ });
    explicitOpen.current = false;
    setOpen(false);
  };

  const next = () => {
    // Marque l'étape courante comme terminée (best-effort, silencieux)
    if (s?.id) markTourStepDone(s.id).catch(() => {});
    if (step >= steps.length - 1) { close(); return; }
    setStep(step + 1);
  };

  if (!open || !user || steps.length === 0) return null;
  const s = steps[step];
  const playlist = Array.isArray(s.playlist) ? s.playlist : [];
  const activeVideo = playlist.length > 0
    ? playlist[Math.min(videoIdx, playlist.length - 1)]?.video_url
    : s.video_url;

  return (
    <Dialog open={open} onOpenChange={(nextOpen) => { if (!nextOpen) close(); }}>
      <DialogContent
        data-testid="app-tour"
        data-tour-dialog="true"
        showCloseButton={false}
        overlayProps={{ className: 'z-[9998] bg-black/50 backdrop-blur-sm', 'data-testid': 'tour-backdrop' }}
        className="z-[9999] block min-w-0 w-[calc(100%_-_2rem)] max-w-md max-h-[calc(100dvh_-_2rem)] rounded-2xl border-0 p-0 bg-white shadow-2xl overflow-y-auto"
      >
      <div className="relative min-w-0" data-testid={`tour-step-${step}`}>
        <div className={`h-1.5 bg-gradient-to-r ${s.tone}`} />
        <button
          onClick={close}
          className="absolute top-3 right-3 text-[#5E6D66] hover:text-[#1A2421] p-1.5 rounded-md hover:bg-[#F5F4EF]"
          data-testid="tour-close"
          aria-label="Fermer"
        >
          <X size={16} weight="bold" />
        </button>
        <div className="p-6 space-y-4">
          <div className="flex items-center gap-2">
            <Sparkle size={16} weight="fill" className="text-[#2D5A4C]" />
            <span className="text-[10px] uppercase tracking-widest text-[#5E6D66] font-bold">Étape {step + 1} / {steps.length}</span>
          </div>
          <DialogTitle data-testid="tour-step-title" className="text-2xl font-extrabold text-[#1A2421] break-words" style={{ fontFamily: 'Manrope' }}>{s.title}</DialogTitle>
          <DialogDescription data-testid="tour-step-description" className="text-sm text-[#5E6D66] leading-relaxed break-words">{s.body}</DialogDescription>
          {activeVideo && (
            <div className="rounded-xl overflow-hidden border border-[#E8E6E1] bg-black" data-testid="tour-video">
              {/youtu\.?be/i.test(activeVideo) ? (
                <iframe
                  key={activeVideo}
                  className="w-full aspect-video"
                  src={activeVideo.replace('watch?v=', 'embed/').replace('youtu.be/', 'www.youtube.com/embed/')}
                  title="Vidéo de l'étape"
                  allow="autoplay; encrypted-media"
                  allowFullScreen
                />
              ) : (
                <video key={activeVideo} src={activeVideo} controls className="w-full aspect-video" preload="metadata" />
              )}
            </div>
          )}
          {playlist.length > 0 && (
            <div className="space-y-1" data-testid="tour-playlist">
              <p className="text-[10px] uppercase tracking-widest text-[#5E6D66] font-bold">Playlist ({playlist.length} vidéos)</p>
              <div className="grid grid-cols-1 gap-1">
                {playlist.map((v, i) => (
                  <button
                    key={i}
                    onClick={() => setVideoIdx(i)}
                    className={`text-left text-xs rounded-md px-2 py-1.5 transition-colors ${i === videoIdx ? 'bg-[#2D5A4C] text-white' : 'bg-[#F5F4EF] text-[#1A2421] hover:bg-[#E8E6E1]'}`}
                    data-testid={`tour-playlist-btn-${i}`}
                  >
                    <span className="font-mono opacity-70 mr-1.5">{i + 1}.</span>{v.title}
                  </button>
                ))}
              </div>
            </div>
          )}

          <div className="flex min-w-0 flex-col sm:flex-row sm:justify-between sm:items-center gap-3 pt-2" data-testid="tour-actions">
            <button onClick={close} className="text-left text-xs text-[#5E6D66] hover:text-[#1A2421] font-medium" data-testid="tour-skip">
              Passer le tour
            </button>
            <div className="flex min-w-0 max-w-full flex-wrap items-center justify-end gap-2">
              {s.action_path && (
                <Button
                  variant="outline"
                  size="sm"
                  className="min-w-0 max-w-full h-auto min-h-9 whitespace-normal break-words py-2"
                  onClick={() => { close(); navigate(s.action_path); }}
                  data-testid="tour-go"
                >
                  {s.action_label}
                </Button>
              )}
              <Button
                size="sm"
                className="shrink-0 bg-[#2D5A4C] hover:bg-[#1E4136]"
                onClick={next}
                data-testid="tour-next"
              >
                {step >= steps.length - 1 ? 'Terminer' : 'Suivant'}
                <ArrowRight size={12} weight="bold" className="ml-1" />
              </Button>
            </div>
          </div>

          <div className="flex justify-center gap-1 pt-2">
            {steps.map((_, i) => (
              <span
                key={i}
                className={`h-1 rounded-full transition-all ${i === step ? 'w-6 bg-[#2D5A4C]' : 'w-2 bg-[#E8E6E1]'}`}
              />
            ))}
          </div>
        </div>
      </div>
      </DialogContent>
    </Dialog>
  );
}
